import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { useLocation, useHistory } from 'react-router';
import objectHash from 'object-hash';

import { GET_PROFILE_SUBSCRIPTIONS } from 'store/queries';

const useFetchProfileSubscriptions = (customerId, maxRetries = 3, retryInterval = 3000) => {
  const location = useLocation();
  const history = useHistory();
  const client = useApolloClient();

  const [retries, setRetries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [timeouts, setTimeouts] = useState([]);
  const [previousSubscriptionHash, setPreviousSubscriptionHash] = useState(null);
  const [waitingForQuery, setWaitingForQuery] = useState(false);

  const [profileSubscription, { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError, called }] = useLazyQuery(
    GET_PROFILE_SUBSCRIPTIONS,
    {
      fetchPolicy: 'network-only'
    }
  );

  const fetchProfileSubscription = useCallback(
    () =>
      profileSubscription({
        variables: {
          customerId: customerId,
          subscriptionSearchTypePast: 'PAST',
          subscriptionSearchTypeCurrent: 'CURRENT'
        }
      }),
    [customerId, profileSubscription]
  );

  useEffect(() => {
    if (customerId) {
      if (!called && client) {
        try {
          const cachedSubscriptions = client.readQuery({
            query: GET_PROFILE_SUBSCRIPTIONS,
            variables: {
              customerId: customerId,
              subscriptionSearchTypePast: 'PAST',
              subscriptionSearchTypeCurrent: 'CURRENT'
            }
          });

          setPreviousSubscriptionHash(cachedSubscriptions?.profile?.subscriptionsHash);
        } catch (_) {
          console.warn(`Cannot fetch previous data from the cache store for customer id: ${customerId}`);
        }
      }

      fetchProfileSubscription();
    }
  }, [called, client, customerId, fetchProfileSubscription]);

  const subscriptions = useMemo(() => {
    const pastSubscriptions = subscriptionData?.profile?.pastSubscriptions ?? [];
    const currentSubscriptions = subscriptionData?.profile?.currentSubscriptions ?? [];
    if (pastSubscriptions.length > 0 || currentSubscriptions.length > 0) {
      return [...pastSubscriptions, ...currentSubscriptions];
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData]);

  useEffect(() => {
    if (subscriptions && subscriptions?.length > 0 && !subscriptionLoading && !waitingForQuery) {
      const subscriptionHash = objectHash(subscriptions);

      if (subscriptionHash !== previousSubscriptionHash || retries >= maxRetries) {
        setLoading(false);
        setRetries(0);
        setPreviousSubscriptionHash(subscriptionHash);

        history.replace({
          ...location,
          state: {
            shouldRetry: false
          }
        });
      } else if (location?.state?.shouldRetry && retries < maxRetries) {
        setLoading(true);
        setWaitingForQuery(true);

        const retryTimeout = setTimeout(() => {
          fetchProfileSubscription();

          setWaitingForQuery(false);
          setRetries((retry) => retry + 1);
        }, retryInterval);

        setTimeouts((state) => [...state, retryTimeout]);
      }

      return () => {
        if (timeouts.length > 0) {
          setTimeouts((state) =>
            state.filter((id) => {
              if (timeouts.includes(id)) {
                clearTimeout(id);
                return true;
              }

              return false;
            })
          );
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProfileSubscription, location?.state?.shouldRetry, subscriptionLoading, subscriptions, waitingForQuery]);

  return {
    subscriptions: !(loading || subscriptionLoading) ? subscriptions : [],
    loading: loading || subscriptionLoading,
    error: subscriptionError
  };
};

export default useFetchProfileSubscriptions;
